<template>
  <div :class="$style.calButtonWrap">
    <BaseButton
      ref="button"
      presentation="strip"
      :class="$style.calButton"
      @click="handleAddToCalendarClick(computedCalendarUrl)"
    >
      <slot><IconAddToCalendar width="14" height="16" /></slot>
    </BaseButton>
  </div>
</template>

<script setup lang="ts">
import type { TypeEventWithoutUnresolvableLinksResponse } from '~/types/contentful'

import { format } from 'date-fns'

const props = defineProps<{
  data: TypeEventWithoutUnresolvableLinksResponse
}>()

const clientTimezone = ref('UTC')
const computedCalendarUrl = computed(() => {
  const startDate = props.data?.fields?.startDatetime
  const startDateAsDate = new Date(startDate || '')
  const endDate = props.data?.fields?.endDatetime
  const endDateAsDate = new Date(endDate || '')

  const DTSTART = format(startDateAsDate, "yyyyMMdd'T'HHmmss")
  const DTEND = format(endDateAsDate, "yyyyMMdd'T'HHmmss")

  const TZID = clientTimezone.value

  const calendarUrl = [
    'BEGIN:VCALENDAR',
    'PRODID:-//Google Inc//Google Calendar 70.9054//EN',
    'VERSION:2.0',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:' + props.data?.fields?.title || '',
    `X-WR-TIMEZONE:${TZID}`,
    'X-WR-CALDESC:' + props.data?.fields?.title || '',
    'BEGIN:VEVENT',
    `DTSTART;TZID=${TZID}:${DTSTART}`,
    `DTEND;TZID=${TZID}:${DTEND}`,
    'SUMMARY:' + props.data?.fields?.title || '',
    'TRANSP:OPAQUE',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\n')

  return calendarUrl
})

const handleAddToCalendarClick = (url: string) => {
  if (url.startsWith('data') || url.startsWith('BEGIN')) {
    const filename = `${props.data?.fields?.title}.ics`
    const blob = new Blob([url], { type: 'text/calendar;charset=utf-8' })

    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } else {
    window.open(url, '_blank')
  }
}

onMounted(() => {
  clientTimezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone
})
</script>

<style lang="scss" module>
.calButton {
  background: var(--white);

  color: #e83411;
  padding: 4px 6px;

  &:focus {
    background: var(--white);
    color: #e83411;
  }

  &:hover {
    background: #e83411;
    color: var(--white);
  }
}
</style>
